import { IMinUserDepricated } from "@mctypes/global.types";
import React from "react";
import { useHistory } from "react-router-dom";
import { Button, Modal, ModalBody, ModalHeader } from "reactstrap";
import { HSpace } from "../../../../../components";
import { ConfirmAbortModal } from "../../../../../components/ConfirmAbortModal/ConfirmAbortModal";
import { ActionIcon, IssueIcon } from "../../../../../components/CustomIcons/CustomIcons";
import { ConnectedFromOtherBoardsRtk } from "../../../../../components/IssueDetails/ConnectedFromOtherBoardsInline";
import {
  addAppModalThunk,
  removeAppModalInRedux,
  setHasUnsavedCommentInRedux,
} from "../../../../../global/globalSlice";
import { useAppDispatch, useAppSelector } from "../../../../../hooks";
import { AppModalEnums } from "../../../../../mc-shared/sharedConstants";
import { getIssuesWithFormulaAdded, issueBoardContainsFormula } from "../../../../../mc-shared/sharedUtils";
import LocalErrorBoundary from "../../../../../util/LocalErrorBoundary";
import { useGetProjectQuery } from "../../../../projectRTK";
import { formatShortDate } from "../../../admin/selectors";
import { useGetIssueBoardsQuery, useGetSingleIssueBoardQuery, useGetSumsByFormulaQuery } from "../../IssueBoardRTK";
import { useDoGetSingleIssueQuery } from "../../issueRTK";
import { getIssueRegisterNameOption } from "../../issueUtils";
import IssueItem from "./IssueItem/IssueItem";

const IssueItemModal: React.FC<{
  itemId: string;
  issueBoardId: string;
  projectId: string;
}> = ({ itemId, issueBoardId, projectId }) => {
  const user = useAppSelector((state) => state.globalReducer.user);

  if (projectId == null) return <div>Feil: prosjektId mangler</div>;

  const dispatch = useAppDispatch();

  const { data, isLoading: issueLoading } = useDoGetSingleIssueQuery(
    {
      issueId: itemId,
      projectId: projectId,
    },
    { skip: itemId == null || projectId == null },
  );

  const { data: issueBoard } = useGetSingleIssueBoardQuery({
    projectId: projectId,
    issueBoardId: issueBoardId,
  });

  const issueBoardContainsFormul = issueBoardContainsFormula(issueBoard);

  const { data: sumsByFormula } = useGetSumsByFormulaQuery(
    {
      issueBoardId: issueBoardId,
      projectId: projectId,
    },
    {
      skip: issueBoardContainsFormul === false,
    },
  );

  const issuesWithFormulaAdded = data
    ? getIssuesWithFormulaAdded({
        issues: [data],
        issueBoard: issueBoard,
        sumsByFormula: sumsByFormula,
      })
    : [];

  const issue = issuesWithFormulaAdded[0] ?? data;

  const { data: project, isLoading: projectLoading } = useGetProjectQuery(projectId);

  const { data: issueBoards, isLoading: issueBoardsLoading } = useGetIssueBoardsQuery({ projectId: projectId });

  const isLoading = issueLoading || projectLoading || issueBoardsLoading;

  const history = useHistory();
  const hasUnsavedComment = useAppSelector((state) => state.globalReducer.hasUnsavedComment);
  const [confirmModalOpen, setConfirmModalOpen] = React.useState(false);

  const closeIssueItemModal = () => {
    if (hasUnsavedComment === true) {
      setConfirmModalOpen(true);
      return;
    }
    history.push(`${history.location.pathname}`);
    dispatch(removeAppModalInRedux(data?._id));
  };

  const onConfirmClose = () => {
    setConfirmModalOpen(false);
    dispatch(setHasUnsavedCommentInRedux(false));
    history.push(`${history.location.pathname}`);
    dispatch(removeAppModalInRedux(data?._id));
  };

  return (
    <>
      <ConfirmAbortModal
        isOpen={confirmModalOpen}
        onCancel={() => setConfirmModalOpen(false)}
        onConfirm={() => onConfirmClose()}
        customText="Du har en ulagret kommentar. Er du sikker på at du vil avbryte?"
        customHeader="Ulagret kommentar"
      />
      <Modal fade={false} style={{ maxWidth: "1000px", width: "80%" }} isOpen toggle={closeIssueItemModal}>
        {data != null && isLoading === false && (
          <>
            {data.taskOrIssue === "ISSUE" && (
              <ModalHeader toggle={closeIssueItemModal}>
                <IssueIcon style={{ marginRight: "6px", marginBottom: "3px" }} />
                {getIssueRegisterNameOption(issueBoard?.issueNameSetting, "singular")} {data.id}: {data.title}
              </ModalHeader>
            )}
            {data.taskOrIssue === "TASK" && (
              <ModalHeader toggle={closeIssueItemModal}>
                <ActionIcon style={{ marginRight: "2px", marginBottom: "4px", fontSize: "1.2em", color: "#b6b6b6" }} />
                {issueBoard?.taskNameSetting} {data.id}: {data.title}
              </ModalHeader>
            )}
            {data.archived === true && (
              <ModalHeader toggle={closeIssueItemModal}>
                <IssueIcon style={{ marginRight: "6px", marginBottom: "3px" }} />
                Slettet
              </ModalHeader>
            )}
            {data.archived === true ? (
              <ModalBody>
                <p>
                  Denne saken ble slettet av {(data.lastUpdatedBy as IMinUserDepricated).name} den{" "}
                  {formatShortDate(data.updatedAt as string)}
                </p>
              </ModalBody>
            ) : (
              <ModalBody>
                {data.taskOrIssue === "TASK" && (
                  <div className="d-flex flex-column p-0 m-0">
                    <div className="d-flex text-center align-content-center">
                      <h4 className="gray-600-text mt-1">
                        Del av {getIssueRegisterNameOption(issueBoard?.issueNameSetting, "singular")?.toLowerCase()}:
                      </h4>
                      <HSpace />
                      <Button
                        style={{ marginTop: "1px" }}
                        className="p-0"
                        color="link"
                        onClick={() =>
                          dispatch(
                            addAppModalThunk({
                              itemId: data.connectedIssue._id,
                              boardId: issueBoardId,
                              projectId: projectId,
                              app: AppModalEnums.ISSUE,
                            }),
                          )
                        }
                      >
                        <h4 className="primary-text" style={{ marginBottom: "4px" }}>
                          <IssueIcon className="mr-1" style={{ fontSize: ".8em", marginBottom: "4px" }} />
                          {data.connectedIssue.id}
                        </h4>
                      </Button>

                      <h4 className="gray-600-text mt-1 text-truncate" style={{ maxWidth: "15ch", marginLeft: "4px" }}>
                        {" "}
                        {data.connectedIssue?.title}
                      </h4>
                      <hr />
                    </div>
                  </div>
                )}
                <ConnectedFromOtherBoardsRtk issueId={data._id} projectId={projectId} />
                {issueBoard != null && (
                  <LocalErrorBoundary id="IssueItem">
                    <IssueItem
                      members={project.members}
                      projectId={projectId}
                      issue={issue}
                      user={user}
                      issueBoard={issueBoard}
                      issueOrTask={issue.taskOrIssue}
                      issueBoards={issueBoards}
                    />
                  </LocalErrorBoundary>
                )}
              </ModalBody>
            )}
          </>
        )}
      </Modal>
    </>
  );
};

export default IssueItemModal;
